/**
 * abox-loaded-app-loader.ts — ABox → LoadedApp reverse transform.
 *
 * The INVERSE of the platform's `cogo.ontology.add_block` decomposition
 * (`cogo-mcp-gateway/handlers/_internal/cogo-json-to-triples.ts`). It reads
 * `cogo.application_ontology` via the SAME RLS-respecting PostgREST path
 * `JsonbAboxSource` uses, then continues PAST `AboxInstance[]` to rebuild a
 * full `LoadedApp` ({ manifest, blocks, data, flows }) that the studio's
 * Preview surface (`preview-frame-content.tsx`) can render.
 *
 * Why a NEW reducer (not `JsonbAboxSource.buildInstance`): per the Task 0
 * static spike (`033-01-SPIKE.md` / `045-01-SPIKE.md`, Verdict B), the live
 * `add_block` path emits ONLY `https://cogo.xyz/ns/v1#hasProperty/<key>`
 * predicates with typed `object_jsonb` objects — NO `rdf:type` /
 * `cobo:blockName` / `cobo:blockType` / `rdfs:label`. `buildInstance` keys on
 * those absent predicates and would reduce every `add_block`-persisted subject
 * to `null`. This loader inverts the actual persisted shape losslessly.
 *
 * Generalized contract (Phase 045, D-045-01/02): the loader reconstructs a
 * `LoadedApp` for EVERY `add_block`-accepted block kind plus the `kind:'flow'`
 * envelope — there is no block-kind / flow-name allowlist. Any subject that
 * cannot be faithfully reconstructed (missing `kind`, missing `block_name`, an
 * unrecognized `kind`, a `block_name` collision, or a flow-name collision)
 * triggers a HARD FAIL — a `[AboxLoadedAppLoader]`-prefixed throw naming the
 * offending subject IRI. There is NO silent skip: a loader that silently drops
 * a real block produces a wrong-but-valid-looking app (threat T-045-01,
 * extends T-033-03). `data` is `{}` and `tokens`/`designSystem` are left
 * undefined — `add_block` persists only block-def + flow triples, never
 * datasource / token / designSystem triples (D-045-05/06).
 *
 * RLS / auth (R-033-02, threat T-033-01): runs under the INJECTED
 * authenticated session SupabaseClient (Plan 033-02 supplies `@/lib/supabase`
 * `supabase`). NEVER the anon-key shape-probe path; reads ONLY
 * `cogo.application_ontology` (never the legacy studio-project jsonb table —
 * see Test 6's `.not.toHaveBeenCalledWith` assertion for the enforced path).
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { App, BlockDef, Flow } from '@cogo/types-v2'
import type { LoadedApp } from '@/features/page-editor/store/types'

const HAS_PROPERTY_PREFIX = 'https://cogo.xyz/ns/v1#hasProperty/'

/**
 * Non-renderable kinds persisted into the SAME `application_ontology` table
 * and `app_id` as the app's blocks/flows — e.g. a `kind:'briefing'` row written
 * by `src/features/design-system/briefing/use-briefing.ts` via `addBlock`.
 *
 * D-046-05: a subject whose `kind` is on this closed allowlist is SKIPPED — it
 * is not loaded into `blocks`/`flows` and does NOT hard-fail. This is a
 * review-driven refinement of D-045-02 (the 045-REVIEW P2 finding — a real
 * operator app deeplinked via `?app_id=` may carry a persisted briefing), NOT
 * a violation: D-045-02's intent — "no SILENT partial load of a block that
 * SHOULD render" — is preserved, because a briefing is not a block. The
 * `createAboxLoadedAppLoader` export signature stays byte-stable (D-045-01).
 */
const KNOWN_SKIP_KINDS = new Set(['briefing'])

// Datatypes emitted by the platform's encodeObjectField
// (cogo-json-to-triples.ts:46-51). The loader inverts each.
const RDF_JSON = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#JSON'
const XSD_INTEGER = 'http://www.w3.org/2001/XMLSchema#integer'
const XSD_DOUBLE = 'http://www.w3.org/2001/XMLSchema#double'
const XSD_BOOLEAN = 'http://www.w3.org/2001/XMLSchema#boolean'

export interface AboxLoadedAppLoaderOpts {
  /** Authenticated session client (R-033-02) — Plan 033-02 supplies it. */
  supabaseClient: SupabaseClient
  tenantId: string
  appId: string
  /** Branch to read; defaults to 'main' (matches application_ontology DEFAULT). */
  branch?: string
}

export interface AboxLoadedAppLoader {
  /** Read + reverse-transform `application_ontology` into a `LoadedApp`. Cached. */
  load(): Promise<LoadedApp>
  /** Drop the cache so the next load() re-queries. */
  invalidate(): void
}

interface OntologyRow {
  subject: string
  predicate: string
  object_value: string | null
  object_jsonb: { value?: unknown; datatype?: string } | null
}

/** A subject reduced to its decoded block-def property bag + recovered id. */
interface DecodedSubject {
  /**
   * The full subject IRI (`<base>/<tenant>/<app>/<branch>/<id>`). Retained so
   * the D-045-02 hard-fail can name the offending subject — `id` alone is not
   * enough (the IRI base/tenant/app/branch are stripped from it). (Pitfall 1.)
   */
  subject: string
  /** Platform-assigned id — last path segment of the subject IRI. */
  id: string
  /** Every `hasProperty/<key>` decoded back to its JS value. */
  props: Record<string, unknown>
}

/**
 * Invert `deriveSubjectIri`: the platform `id` is the last (URI-decoded) path
 * segment of `<base>/<tenant>/<app>/<branch>/<id>`.
 */
function recoverIdFromSubject(subject: string): string {
  const idx = Math.max(subject.lastIndexOf('#'), subject.lastIndexOf('/'))
  const tail = idx >= 0 ? subject.slice(idx + 1) : subject
  try {
    return decodeURIComponent(tail)
  } catch {
    return tail
  }
}

/**
 * Invert `encodeObjectField`: read `object_jsonb.{value,datatype}` back to the
 * original JS value. Tolerates the legacy `{value}`-without-datatype shape
 * (forward-compat with `abox-source-jsonb`'s readObject) and the rare
 * `object_value` IRI/bnode shape.
 */
function decodeObject(row: OntologyRow): unknown {
  if (row.object_value !== null) return row.object_value
  const oj = row.object_jsonb
  if (!oj || !('value' in oj)) return undefined

  const { value, datatype } = oj
  switch (datatype) {
    case RDF_JSON:
      // value is already the parsed JS object/array — order preserved by
      // construction (the platform stored the whole nested value inline).
      return value
    case XSD_INTEGER:
    case XSD_DOUBLE:
      return typeof value === 'string' ? Number(value) : value
    case XSD_BOOLEAN:
      return value === 'true' || value === true
    default:
      // xsd:string OR legacy untyped — pass through.
      return value
  }
}

/** Group rows by subject, then decode each subject's hasProperty bag. */
function decodeSubjects(rows: OntologyRow[]): DecodedSubject[] {
  const bySubject = new Map<string, OntologyRow[]>()
  for (const row of rows) {
    const list = bySubject.get(row.subject) ?? []
    list.push(row)
    bySubject.set(row.subject, list)
  }

  const out: DecodedSubject[] = []
  for (const [subject, triples] of bySubject) {
    const id = recoverIdFromSubject(subject)
    if (!id) continue
    const props: Record<string, unknown> = {}
    for (const t of triples) {
      if (!t.predicate.startsWith(HAS_PROPERTY_PREFIX)) continue
      const rawKey = t.predicate.slice(HAS_PROPERTY_PREFIX.length)
      let key: string
      try {
        key = decodeURIComponent(rawKey)
      } catch {
        key = rawKey
      }
      const decoded = decodeObject(t)
      if (decoded !== undefined) props[key] = decoded
    }
    out.push({ subject, id, props })
  }
  return out
}

/**
 * Synthesize a minimal type-valid v2 BlockDef from a decoded subject.
 *
 * The platform `id` is retained at `meta.aboxId` (§2.2 — id retained while the
 * map is keyed by block_name; Test 4 depends on it). A minimal best-effort
 * `ui.root` is produced from `properties` so the Preview renderer has
 * something to mount (renderable-`ui` fidelity is Phase 046 — D-045-08).
 *
 * The full decoded platform envelope `{ id, kind, block_name, block_type,
 * properties }` is carried verbatim at `data.__aboxEnvelope` (D-045-07) — one
 * retrievable object so the round-trip suite (Plan 02) can deep-equal it
 * against the original Coogo JSON and prove nothing was dropped.
 */
function toBlockDef(sub: DecodedSubject, blockName: string): BlockDef {
  // `block_type` is a snake_case DB-field-origin key — preserved verbatim
  // (CLAUDE.md naming rules; do NOT camelCase persisted envelope keys).
  const blockType = String(sub.props.block_type ?? blockName)
  const properties = (sub.props.properties ?? {}) as Record<string, unknown>

  const root = {
    id: `${blockName}-root`,
    type: blockType,
    ...(typeof properties.text === 'string' ? { text: properties.text } : {}),
    ...(typeof properties.placeholder === 'string'
      ? { placeholder: properties.placeholder }
      : {}),
  }

  // BlockDef shape mirrors template-loader.ts:226-235. `aboxId` carries the
  // platform id; the full decoded envelope is preserved verbatim under data
  // for the D-045-07 round-trip proof.
  const meta = {
    name: blockName,
    type: 'ui' as const,
    version: '1.0.0',
    description: `ABox-loaded ${blockName} block`,
    stateContract: { localState: {}, inputSymbols: [], outputSymbols: [] },
    // Platform-assigned id retained as a property (Test 4 / §2.2).
    aboxId: sub.id,
  }

  // The full decoded platform block-def envelope — the exact shape `add_block`
  // accepts. `__aboxProperties` is kept (legacy CLOSE-01 stash); `__aboxEnvelope`
  // is the D-045-07 superset the round-trip test reads.
  const aboxEnvelope = {
    id: sub.id,
    kind: 'block',
    block_name: blockName,
    block_type: blockType,
    properties,
  }

  return {
    meta,
    ui: { schemaVersion: '3.0', blockType, root },
    events: {},
    flow: {},
    state: { state: {} },
    // The persisted-envelope ↔ v2 `BlockDef['data']` shape mismatch is bridged
    // by a localized, documented `as unknown as` cast — the minimal type-bridge
    // (CLAUDE.md Bug-Fix rules: no magic numbers); the round-trip test is the
    // real contract.
    data: {
      __aboxProperties: properties,
      __aboxEnvelope: aboxEnvelope,
    } as unknown as BlockDef['data'],
  } as unknown as BlockDef
}

/**
 * Reconstruct a Flow from a `kind:'flow'` subject. The persisted envelope
 * (`name` / `trigger:{event,target}` / `steps:[{step,action,...}]`) is
 * preserved verbatim for round-trip; the v2 `Flow.trigger` discriminant is set
 * to `'event'` (the persisted trigger object is an event binding).
 */
function toFlow(sub: DecodedSubject): { name: string; flow: Flow } {
  const name = String(sub.props.name ?? sub.id)
  const steps = Array.isArray(sub.props.steps) ? sub.props.steps : []
  const flow = {
    name,
    trigger: 'event' as const,
    steps,
    // Preserve the raw persisted trigger envelope for faithful round-trip.
    _aboxTrigger: sub.props.trigger,
    _aboxId: sub.id,
  } as unknown as Flow
  return { name, flow }
}

/**
 * Synthesize a minimal type-valid `App` manifest with ONE page referencing the
 * loaded blocks so `manifest.pages.length > 0` AND the page mounts the blocks
 * (preview-frame-content.tsx:91-93 `hasBlocks && hasPages`). Manifest-synthesis
 * idiom mirrors template-loader.ts:183-192.
 */
function synthesizeManifest(appId: string, blockNames: string[]): App {
  const blockLayout: Record<string, { width: string }> = {}
  for (const n of blockNames) blockLayout[n] = { width: 'full' }

  return {
    appId,
    name: appId,
    description: 'ABox-loaded application',
    version: '1.0.0',
    schemaVersion: '3.0',
    navigation: { defaultRoute: '/' },
    pages: [
      {
        id: 'main',
        route: '/',
        name: 'Main',
        layout: 'flow',
        blocks: blockNames,
        blockLayout,
      },
    ],
    globalState: {},
  } as unknown as App
}

export function createAboxLoadedAppLoader(
  opts: AboxLoadedAppLoaderOpts,
): AboxLoadedAppLoader {
  const branch = opts.branch ?? 'main'
  // Per-instance cache (NOT a module singleton — use-ontology-graph.ts:81
  // documents why module-singleton is wrong for tenant-scoped reads).
  let cache: Promise<LoadedApp> | null = null

  function invalidate(): void {
    cache = null
  }

  async function fetchRows(): Promise<OntologyRow[]> {
    const { data, error } = await opts.supabaseClient
      .schema('cogo')
      .from('application_ontology')
      .select('subject, predicate, object_value, object_jsonb')
      .eq('tenant_id', opts.tenantId)
      .eq('app_id', opts.appId)
      .eq('branch', branch)
    if (error) {
      // Threat T-033-03: NEVER silently return an empty/partial LoadedApp —
      // a silent empty renders the "No pages" placeholder and falsely passes
      // the downstream ACCEPT-01 spec.
      throw new Error(
        `[AboxLoadedAppLoader] PostgREST error: ${
          error.message ?? JSON.stringify(error)
        }`,
      )
    }
    return (data as OntologyRow[]) ?? []
  }

  /**
   * Reverse-transform decoded subjects into a `LoadedApp`. Generalized per
   * D-045-02: every `add_block`-accepted block kind loads, every `kind:'flow'`
   * subject routes to `flows` — no allowlist. Any subject that cannot be
   * faithfully reconstructed HARD-FAILS with a `[AboxLoadedAppLoader]`-prefixed
   * throw naming the offending subject IRI (threat T-045-01 — no silent
   * partial). The CLOSE-01 warn-and-skip branches are gone.
   */
  function buildLoadedApp(rows: OntologyRow[]): LoadedApp {
    const subjects = decodeSubjects(rows)

    const blocks: Record<string, BlockDef> = {}
    const flows: Record<string, Flow> = {}

    for (const sub of subjects) {
      const kind = String(sub.props.kind ?? '')
      if (!kind) {
        throw new Error(
          `[AboxLoadedAppLoader] subject <${sub.subject}> has no 'kind' — cannot reconstruct`,
        )
      }

      if (kind === 'flow') {
        const { name, flow } = toFlow(sub)
        if (flows[name]) {
          throw new Error(
            `[AboxLoadedAppLoader] flow name collision '${name}' at <${sub.subject}>`,
          )
        }
        flows[name] = flow
        continue
      }

      if (kind === 'block') {
        const blockName = String(sub.props.block_name ?? '')
        if (!blockName) {
          throw new Error(
            `[AboxLoadedAppLoader] block subject <${sub.subject}> has no 'block_name'`,
          )
        }
        if (blocks[blockName]) {
          throw new Error(
            `[AboxLoadedAppLoader] block_name collision '${blockName}' at <${sub.subject}>`,
          )
        }
        // Keyed by block_name, NOT the platform id (§2.2 / Test 1).
        blocks[blockName] = toBlockDef(sub, blockName)
        continue
      }

      // D-046-05: a KNOWN non-render kind (e.g. a kind:'briefing' row written
      // by use-briefing.ts into the same application_ontology table/app_id).
      // Not loaded into blocks/flows; does NOT hard-fail — D-045-02's "no
      // SILENT partial load of a block that SHOULD render" intent is preserved
      // because a briefing is not a block.
      if (KNOWN_SKIP_KINDS.has(kind)) {
        continue
      }

      // D-046-06: a genuinely-unrecognized kind — one that is neither `block`,
      // nor `flow`, nor on the KNOWN_SKIP_KINDS allowlist — still HARD-FAILS.
      // No decoder handles it; the D-045-02 hard-fail is kept verbatim.
      throw new Error(
        `[AboxLoadedAppLoader] subject <${sub.subject}> has unrecognized kind '${kind}'`,
      )
    }

    const manifest = synthesizeManifest(opts.appId, Object.keys(blocks))

    return {
      manifest,
      blocks,
      // `add_block` persists no datasource triples — `data` is genuinely empty
      // (D-045-06; the former example-specific hardcode did not generalize).
      // `tokens` and `designSystem` are omitted — never persisted (D-045-05);
      // both optional on `LoadedApp`.
      data: {},
      flows,
    }
  }

  async function load(): Promise<LoadedApp> {
    if (cache) return cache
    cache = (async () => buildLoadedApp(await fetchRows()))()
    try {
      return await cache
    } catch (e) {
      cache = null
      throw e
    }
  }

  return { load, invalidate }
}
